import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import useAxiosAuth from '../hooks/useAxiosAuth';
import baseURL from '../utils/baseURL';

// ==================== CATEGORY CRUD ====================

// Create Category
export const createCategory = createAsyncThunk(
    'master/createCategory',
    async ({ name, role }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.post(`${baseURL}/support/category`, {
                name,
                role
            });
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to create category'
            });
        }
    }
);

// Get Categories
// Support categories for the raise page. The server derives the audience from
// the JWT (admin / doctor / staff / patient), so a logged-in caller passes no
// role. `role` survives only as the tokenless legacy hint (`?role=doctor`).
export const getCategories = createAsyncThunk(
    'master/getCategories',
    async (role, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const url = role ? `${baseURL}/support/category?role=${role}` : `${baseURL}/support/category`;
            const response = await axiosInstance.get(url);
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to fetch categories'
            });
        }
    }
);

// Update Category
export const updateCategory = createAsyncThunk(
    'master/updateCategory',
    async ({ categoryId, name, role }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.put(`${baseURL}/support/category/${categoryId}`, {
                name,
                role
            });
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to update category'
            });
        }
    }
);

// Delete Category
export const deleteCategory = createAsyncThunk(
    'master/deleteCategory',
    async (categoryId, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.delete(`${baseURL}/support/category/${categoryId}`);
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to delete category'
            });
        }
    }
);

// ==================== SUBCATEGORY CRUD ====================

// Create Subcategory
export const createSubcategory = createAsyncThunk(
    'master/createSubcategory',
    async ({ categoryId, name, description, role }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.post(`${baseURL}/support/subcategory`, {
                categoryId,
                name,
                description,
                role
            });
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to create subcategory'
            });
        }
    }
);

// Get Subcategories by Category
export const getSubcategoriesByCategory = createAsyncThunk(
    'master/getSubcategoriesByCategory',
    async ({ categoryId, role }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            // Same audience rule as getCategories: no role when logged in.
            const url = role ? `${baseURL}/support/subcategory/category/${categoryId}?role=${role}` : `${baseURL}/support/subcategory/category/${categoryId}`;
            const response = await axiosInstance.get(url);
            return { categoryId, data: response.data };
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to fetch subcategories'
            });
        }
    }
);

// Fetch ALL support subcategories as a flat list. Used by the admin support page as
// a name-based fallback to resolve subCategoryId from the ticket's subject snapshot
// (which stores the subcategory name). Cheap — one round trip per admin mount.
export const getAllSupportSubCategories = createAsyncThunk(
    'master/getAllSupportSubCategories',
    async (_, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`${baseURL}/support/subcategory`);
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to fetch subcategories'
            });
        }
    }
);

// Update Subcategory
export const updateSubcategory = createAsyncThunk(
    'master/updateSubcategory',
    async ({ subcategoryId, categoryId, name, description, role }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.put(`${baseURL}/support/subcategory/${subcategoryId}`, {
                categoryId,
                name,
                description,
                role
            });
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to update subcategory'
            });
        }
    }
);

// Delete Subcategory
export const deleteSubcategory = createAsyncThunk(
    'master/deleteSubcategory',
    async (subcategoryId, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.delete(`${baseURL}/support/subcategory/${subcategoryId}`);
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to delete subcategory'
            });
        }
    }
);

// ==================== SPECIALITY CATEGORY CRUD ====================

// Create Speciality Category
export const createSpCategory = createAsyncThunk(
    'master/createSpCategory',
    async ({ spcategoryName }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.post(`${baseURL}/speciality-category`, {
                spcategoryName
            });
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to create speciality category'
            });
        }
    }
);

// Get Speciality Categories (paginated)
export const getSpCategories = createAsyncThunk(
    'master/getSpCategories',
    async ({ page = 1, limit = 10, search = '', status = '' } = {}, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const params = new URLSearchParams({ page, limit });
            if (search) params.append('search', search);
            if (status === 'active') params.append('status', 'true');
            else if (status === 'inactive') params.append('status', 'false');
            const response = await axiosInstance.get(`${baseURL}/speciality-category?${params.toString()}`);
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to fetch speciality categories'
            });
        }
    }
);

// Update Speciality Category
export const updateSpCategory = createAsyncThunk(
    'master/updateSpCategory',
    async ({ id, spcategoryName }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.put(`${baseURL}/speciality-category/${id}`, {
                spcategoryName
            });
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to update speciality category'
            });
        }
    }
);

// Delete Speciality Category (soft delete)
export const deleteSpCategory = createAsyncThunk(
    'master/deleteSpCategory',
    async (id, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.delete(`${baseURL}/speciality-category/${id}`);
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to delete speciality category'
            });
        }
    }
);

// Toggle Speciality Category Status
export const toggleSpCategoryStatus = createAsyncThunk(
    'master/toggleSpCategoryStatus',
    async ({ id }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.post(`${baseURL}/speciality-category/${id}/toggle-status`);
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to toggle speciality category status'
            });
        }
    }
);

// Check if Speciality Category or Speciality is Used by Doctors
export const checkUsedByDoctors = createAsyncThunk(
    'master/checkUsedByDoctors',
    async ({ master, id }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.post(`${baseURL}/speciality-category/check-used-by-doctors`, { master, id });
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to check usage by doctors'
            });
        }
    }
);

// Check Speciality Category Name Duplicate
export const checkSpCategoryName = createAsyncThunk(
    'master/checkSpCategoryName',
    async (spcategoryName, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`${baseURL}/speciality-category/check-duplicate?spcategoryName=${encodeURIComponent(spcategoryName)}`);
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to check speciality category name'
            });
        }
    }
);

// Get Active Speciality Category List (for dropdown)
export const getSpCategoryList = createAsyncThunk(
    'master/getSpCategoryList',
    async (search = '', { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const url = search
                ? `${baseURL}/speciality-category/list?search=${encodeURIComponent(search)}`
                : `${baseURL}/speciality-category/list`;
            const response = await axiosInstance.get(url);
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to fetch speciality category list'
            });
        }
    }
);

// Download Sample Speciality Category Excel Template
export const downloadSpCategorySampleExcel = createAsyncThunk(
    'master/downloadSpCategorySampleExcel',
    async (_, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`${baseURL}/speciality-category/download-sample-excel`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'sample_speciality_category_template.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            return { success: true };
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to download sample excel'
            });
        }
    }
);

// Bulk Upload Speciality Categories from Excel
export const bulkUploadSpCategories = createAsyncThunk(
    'master/bulkUploadSpCategories',
    async (file, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const formData = new FormData();
            formData.append('file', file);
            const response = await axiosInstance.post(`${baseURL}/speciality-category/bulk-upload`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to bulk upload speciality categories'
            });
        }
    }
);

// Check Speciality Category Excel format (called immediately on file select)
export const checkSpCategoryBulkFormat = createAsyncThunk(
    'master/checkSpCategoryBulkFormat',
    async (file, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const formData = new FormData();
            formData.append('file', file);
            const response = await axiosInstance.post(`${baseURL}/speciality-category/check-bulk-format`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            return response.data;
        } catch (error) {
            return rejectWithValue({
                valid: false,
                error: error.response?.data?.message || error.message || 'Invalid Excel format',
                errors: error.response?.data?.errors
            });
        }
    }
);

// ==================== SPECIALTY CRUD ====================

// Create Specialty
export const createSpecialty = createAsyncThunk(
    'master/createSpecialty',
    async ({ name, description, categoryId }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.post(`${baseURL}/speciality`, {
                name,
                description,
                categoryId
            });
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to create specialty'
            });
        }
    }
);

// Get Specialties (with pagination, search, status and category filter)
export const getSpecialties = createAsyncThunk(
    'master/getSpecialties',
    async ({ page = 1, limit = 12, search = '', status = '', categoryId = '' } = {}, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const params = new URLSearchParams({ page, limit });
            if (search) params.append('search', search);
            if (status) params.append('status', status);
            if (categoryId) params.append('categoryId', categoryId);
            const response = await axiosInstance.get(`${baseURL}/speciality?${params.toString()}`);
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to fetch specialties'
            });
        }
    }
);

// Get Specialty by ID
export const getSpecialtyById = createAsyncThunk(
    'master/getSpecialtyById',
    async (id, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`${baseURL}/speciality/${id}`);
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to fetch specialty'
            });
        }
    }
);

// Update Specialty
export const updateSpecialty = createAsyncThunk(
    'master/updateSpecialty',
    async ({ id, name, description, categoryId }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const body = { name, description };
            if (categoryId) body.categoryId = categoryId;
            const response = await axiosInstance.put(`${baseURL}/speciality/${id}`, body);
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to update specialty'
            });
        }
    }
);

// Delete Specialty
export const deleteSpecialty = createAsyncThunk(
    'master/deleteSpecialty',
    async (id, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.delete(`${baseURL}/speciality/${id}`);
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to delete specialty'
            });
        }
    }
);

// Check if Specialty is Used by Doctors
export const checkDoctorUsage = createAsyncThunk(
    'master/checkDoctorUsage',
    async (id, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`${baseURL}/speciality/${id}/check-doctor-usage`);
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to check doctor usage'
            });
        }
    }
);

// Toggle Specialty Status (active/inactive)
export const toggleSpecialtyStatus = createAsyncThunk(
    'master/toggleSpecialtyStatus',
    async ({ id, status }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.post(`${baseURL}/speciality/${id}/toggle-status`, {
                status
            });
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to toggle specialty status'
            });
        }
    }
);

// Get All Specialty List
export const getallspecilitylist = createAsyncThunk(
    'master/getallspecilitylist',
    async ({ search = '', categoryId = '' } = {}, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const params = new URLSearchParams();
            if (search) params.append('search', search);
            if (categoryId) params.append('categoryId', categoryId);
            const queryString = params.toString();
            const url = queryString
                ? `${baseURL}/speciality/list?${queryString}`
                : `${baseURL}/speciality/list`;
            const response = await axiosInstance.get(url);
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to fetch specialty list'
            });
        }
    }
);

// Check Specialty Name Duplicate
export const checkSpecilityName = createAsyncThunk(
    'master/checkSpecilityName',
    async (name, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`${baseURL}/speciality/check-duplicate?name=${encodeURIComponent(name)}`);
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to check specialty name'
            });
        }
    }
);

// Download Sample Specialty Excel Template
export const downloadSpecialtySampleExcel = createAsyncThunk(
    'master/downloadSpecialtySampleExcel',
    async (_, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`${baseURL}/speciality/download-sample-excel`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'sample_speciality_template.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            return { success: true };
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to download sample excel'
            });
        }
    }
);

// Bulk Upload Specialties from Excel
export const bulkUploadSpecialties = createAsyncThunk(
    'master/bulkUploadSpecialties',
    async (file, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const formData = new FormData();
            formData.append('file', file);
            const response = await axiosInstance.post(`${baseURL}/speciality/bulk-upload`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to bulk upload specialties'
            });
        }
    }
);

// Check Speciality Excel format (called immediately on file select)
export const checkSpecialtyBulkFormat = createAsyncThunk(
    'master/checkSpecialtyBulkFormat',
    async (file, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const formData = new FormData();
            formData.append('file', file);
            const response = await axiosInstance.post(`${baseURL}/speciality/check-bulk-format`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            return response.data;
        } catch (error) {
            return rejectWithValue({
                valid: false,
                error: error.response?.data?.message || error.message || 'Invalid Excel format',
                errors: error.response?.data?.errors
            });
        }
    }
);

// ==================== PROFESSION CRUD ====================

// Create Profession
export const createProfession = createAsyncThunk(
    'master/createProfession',
    async ({ professionName }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.post(`${baseURL}/profession`, {
                professionName
            });
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to create profession'
            });
        }
    }
);

// Get Professions (with pagination, search, and status filter)
export const getProfessions = createAsyncThunk(
    'master/getProfessions',
    async ({ page = 1, limit = 12, search = '', status = '' } = {}, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const params = new URLSearchParams({ page, limit });
            if (search) params.append('search', search);
            if (status === 'active') params.append('status', 'true');
            else if (status === 'inactive') params.append('status', 'false');
            const response = await axiosInstance.get(`${baseURL}/profession?${params.toString()}`);
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to fetch professions'
            });
        }
    }
);

// Get Profession by ID
export const getProfessionById = createAsyncThunk(
    'master/getProfessionById',
    async (id, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`${baseURL}/profession/${id}`);
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to fetch profession'
            });
        }
    }
);

// Update Profession
export const updateProfession = createAsyncThunk(
    'master/updateProfession',
    async ({ id, professionName }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.put(`${baseURL}/profession/${id}`, {
                professionName
            });
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to update profession'
            });
        }
    }
);

// Delete Profession (Soft Delete)
export const deleteProfession = createAsyncThunk(
    'master/deleteProfession',
    async (id, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.delete(`${baseURL}/profession/${id}`);
            return response.data;
        } catch (error) {
            const data = error.response?.data || {};
            return rejectWithValue({
                error: data.message || error.message || 'Failed to delete profession',
                status: error.response?.status,
                inUse: !!data.inUse,
                assignedCount: data.assignedCount
            });
        }
    }
);

// Toggle Profession Status
export const toggleProfessionStatus = createAsyncThunk(
    'master/toggleProfessionStatus',
    async ({ id }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.post(`${baseURL}/profession/${id}/toggle-status`);
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to toggle profession status'
            });
        }
    }
);

// Check Profession Name Duplicate
export const checkProfessionName = createAsyncThunk(
    'master/checkProfessionName',
    async (professionName, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`${baseURL}/profession/check-duplicate?professionName=${encodeURIComponent(professionName)}`);
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to check profession name'
            });
        }
    }
);

// Download Sample Profession Excel Template
export const downloadProfessionSampleExcel = createAsyncThunk(
    'master/downloadProfessionSampleExcel',
    async (_, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`${baseURL}/profession/download-sample-excel`, {
                responseType: 'blob'
            });
            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'sample_profession_template.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            return { success: true };
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to download sample excel'
            });
        }
    }
);

// Bulk Upload Professions from Excel
export const bulkUploadProfessions = createAsyncThunk(
    'master/bulkUploadProfessions',
    async (file, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const formData = new FormData();
            formData.append('file', file);
            const response = await axiosInstance.post(`${baseURL}/profession/bulk-upload`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to bulk upload professions'
            });
        }
    }
);

// Check Profession Excel format (called immediately on file select; no DB writes)
export const checkProfessionBulkFormat = createAsyncThunk(
    'master/checkProfessionBulkFormat',
    async (file, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const formData = new FormData();
            formData.append('file', file);
            const response = await axiosInstance.post(`${baseURL}/profession/check-bulk-format`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            return response.data;
        } catch (error) {
            return rejectWithValue({
                valid: false,
                error: error.response?.data?.message || error.message || 'Invalid Excel format',
                errors: error.response?.data?.errors
            });
        }
    }
);

// Get All Professions List (for dropdowns - no pagination)
export const getAllProfessionsList = createAsyncThunk(
    'master/getAllProfessionsList',
    async (_, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`${baseURL}/profession?limit=500&status=true`);
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to fetch profession list'
            });
        }
    }
);

// ==================== COUNTRY CRUD ====================

// Get Countries (with pagination, search, and status filter)
export const getCountries = createAsyncThunk(
    'master/getCountries',
    async ({ page = 1, limit = 10, search = '', status = '' } = {}, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const params = new URLSearchParams({ page, limit });
            if (search) params.append('search', search);
            if (status) params.append('status', status);
            const response = await axiosInstance.get(`${baseURL}/country?${params.toString()}`);
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to fetch countries'
            });
        }
    }
);

// Get Country by ID
export const getCountryById = createAsyncThunk(
    'master/getCountryById',
    async (id, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`${baseURL}/country/${id}`);
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to fetch country'
            });
        }
    }
);

// Create Country (multipart/form-data for flag image)
export const createCountry = createAsyncThunk(
    'master/createCountry',
    async (formData, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.post(`${baseURL}/country`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to create country'
            });
        }
    }
);

// Update Country (multipart/form-data for flag image)
export const updateCountry = createAsyncThunk(
    'master/updateCountry',
    async ({ id, formData }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.put(`${baseURL}/country/${id}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to update country'
            });
        }
    }
);

// Delete Country (soft delete)
export const deleteCountry = createAsyncThunk(
    'master/deleteCountry',
    async (id, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.delete(`${baseURL}/country/${id}`);
            return response.data;
        } catch (error) {
            const data = error.response?.data || {};
            return rejectWithValue({
                error: data.message || error.message || 'Failed to delete country',
                status: error.response?.status,
                inUse: !!data.inUse,
                assignedCount: data.assignedCount,
                doctorCount: data.doctorCount,
                patientCount: data.patientCount
            });
        }
    }
);

// Toggle Country Status
export const toggleCountryStatus = createAsyncThunk(
    'master/toggleCountryStatus',
    async ({ id, status }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.post(`${baseURL}/country/${id}/toggle-status`, { status });
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to toggle country status'
            });
        }
    }
);

// Check Country Name Duplicate
export const checkCountryNameDuplicate = createAsyncThunk(
    'master/checkCountryNameDuplicate',
    async (name, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`${baseURL}/country/check-duplicate?name=${encodeURIComponent(name)}`);
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to check country name'
            });
        }
    }
);

// Get Country List with Timezones (for doctor/patient creation & registration)
export const docPatCreationCountryList = createAsyncThunk(
    'master/docPatCreationCountryList',
    async (_, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`${baseURL}/country/list-with-timezones`);
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to fetch country timezone list'
            });
        }
    }
);

// ==================== COUNTRY BULK UPLOAD ====================

// Download the country sample Excel (pre-filled with all 197 countries).
export const downloadCountrySampleExcel = createAsyncThunk(
    'master/downloadCountrySampleExcel',
    async (_, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`${baseURL}/country/download-sample-excel`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'sample_country_template.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            return { success: true };
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to download country sample'
            });
        }
    }
);

// Validate country bulk upload format only (no DB writes).
export const checkCountryBulkFormat = createAsyncThunk(
    'master/checkCountryBulkFormat',
    async (file, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const formData = new FormData();
            formData.append('file', file);
            const response = await axiosInstance.post(`${baseURL}/country/check-bulk-format`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            return response.data;
        } catch (error) {
            return rejectWithValue({
                valid: false,
                error: error.response?.data?.message || error.message || 'Invalid Excel format',
                errors: error.response?.data?.errors
            });
        }
    }
);

// Bulk create countries from Excel.
export const bulkUploadCountries = createAsyncThunk(
    'master/bulkUploadCountries',
    async (file, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const formData = new FormData();
            formData.append('file', file);
            const response = await axiosInstance.post(`${baseURL}/country/bulk-upload`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to bulk upload countries'
            });
        }
    }
);

// ==================== SUPPORT BULK UPLOAD ====================

// Download the support sample Excel (workbook with two sheets:
// Categories + SubCategories).
export const downloadSupportSampleExcel = createAsyncThunk(
    'master/downloadSupportSampleExcel',
    async (_, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`${baseURL}/support/download-sample-excel`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'sample_support_template.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            return { success: true };
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to download support sample'
            });
        }
    }
);

// Validate support bulk upload format only (no DB writes). The backend
// confirms both Categories and SubCategories sheets are present and have
// the required headers.
export const checkSupportBulkFormat = createAsyncThunk(
    'master/checkSupportBulkFormat',
    async (file, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const formData = new FormData();
            formData.append('file', file);
            const response = await axiosInstance.post(`${baseURL}/support/check-bulk-format`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            return response.data;
        } catch (error) {
            return rejectWithValue({
                valid: false,
                error: error.response?.data?.message || error.message || 'Invalid Excel format',
                errors: error.response?.data?.errors
            });
        }
    }
);

// Bulk create support categories + sub-categories from the two-sheet workbook.
export const bulkUploadSupport = createAsyncThunk(
    'master/bulkUploadSupport',
    async (file, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const formData = new FormData();
            formData.append('file', file);
            const response = await axiosInstance.post(`${baseURL}/support/bulk-upload`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data?.message || error.message || 'Failed to bulk upload support'
            });
        }
    }
);

// ==================== REDUX SLICE ====================

const masterSlice = createSlice({
    name: 'master',
    initialState: {
        categories: [],
        subcategories: {},
        supportCategories: [], // Support categories for the raise page (pages/support/RaiseSupport.jsx)
        allSupportSubCategories: [], // Flat list used for name-based lookups
        specialties: [],
        specialtyPagination: {
            total: 0,
            page: 1,
            limit: 5,
            totalPages: 0
        },
        loading: false,
        categoryLoading: false,
        subcategoryLoading: false,
        supportCategoryLoading: false,
        specialtyLoading: false,
        specialtyCreateLoading: false,
        specialtyUpdateLoading: false,
        specialtyDeleteLoading: false,
        specialtyToggleLoading: false,
        specialtyNameCheckLoading: false,
        specialtyNameCheckResult: null,
        specialtyBulkUploadLoading: false,
        specialtyDownloadLoading: false,
        // Speciality Category state
        spCategories: [],
        spCategoryPagination: {
            total: 0,
            page: 1,
            limit: 10,
            totalPages: 0
        },
        spCategoryLoading: false,
        spCategoryCreateLoading: false,
        spCategoryUpdateLoading: false,
        spCategoryDeleteLoading: false,
        spCategoryToggleLoading: false,
        spCategoryNameCheckLoading: false,
        spCategoryNameCheckResult: null,
        spCategoryBulkUploadLoading: false,
        spCategoryDownloadLoading: false,
        spCategoryList: [],
        spCategoryListLoading: false,
        // Profession state
        professions: [],
        professionPagination: {
            total: 0,
            page: 1,
            limit: 5,
            totalPages: 0
        },
        professionLoading: false,
        professionCreateLoading: false,
        professionUpdateLoading: false,
        professionDeleteLoading: false,
        professionToggleLoading: false,
        professionNameCheckLoading: false,
        professionNameCheckResult: null,
        professionBulkUploadLoading: false,
        professionDownloadLoading: false,
        professionListAll: [],
        professionListAllLoading: false,
        // Country state
        countries: [],
        countryPagination: {
            total: 0,
            page: 1,
            limit: 10,
            totalPages: 0
        },
        countryLoading: false,
        countryCreateLoading: false,
        countryUpdateLoading: false,
        countryDeleteLoading: false,
        countryToggleLoading: false,
        countryBulkUploadLoading: false,
        countryDownloadLoading: false,
        supportBulkUploadLoading: false,
        supportDownloadLoading: false,
        countryNameCheckLoading: false,
        countryNameCheckResult: null,
        // Country timezone list (for doctor/patient creation & registration)
        countryTimezoneList: [],
        countryTimezoneLoading: false,
        error: null,
        success: false
    },
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearSuccess: (state) => {
            state.success = false;
        },
        clearSubcategories: (state) => {
            state.subcategories = {};
        },
        clearSpecialtyNameCheck: (state) => {
            state.specialtyNameCheckResult = null;
        },
        clearSpCategoryNameCheck: (state) => {
            state.spCategoryNameCheckResult = null;
        },
        clearProfessionNameCheck: (state) => {
            state.professionNameCheckResult = null;
        },
        clearCountryNameCheck: (state) => {
            state.countryNameCheckResult = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // Create Category
            .addCase(createCategory.pending, (state) => {
                state.categoryLoading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(createCategory.fulfilled, (state, action) => {
                state.categoryLoading = false;
                state.success = true;
                // Refresh categories list
            })
            .addCase(createCategory.rejected, (state, action) => {
                state.categoryLoading = false;
                state.error = action.payload?.error || 'Failed to create category';
            })
            // Get Categories
            // The slot used to depend on whether a role was passed (role → raise
            // page, none → the master page). The master page lives in the Super
            // Admin app now and the raise page no longer sends a role, so there is
            // exactly one consumer and one slot.
            .addCase(getCategories.pending, (state) => {
                state.supportCategoryLoading = true;
                state.error = null;
            })
            .addCase(getCategories.fulfilled, (state, action) => {
                // Handle API response structure: { categories: [...] } or { data: [...] }
                const categories = action.payload?.categories || action.payload?.data || (Array.isArray(action.payload) ? action.payload : []);
                state.supportCategoryLoading = false;
                state.supportCategories = categories;
            })
            .addCase(getCategories.rejected, (state, action) => {
                state.supportCategoryLoading = false;
                state.error = action.payload?.error || 'Failed to fetch categories';
            })
            // Update Category
            .addCase(updateCategory.pending, (state) => {
                state.categoryLoading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(updateCategory.fulfilled, (state, action) => {
                state.categoryLoading = false;
                state.success = true;
            })
            .addCase(updateCategory.rejected, (state, action) => {
                state.categoryLoading = false;
                state.error = action.payload?.error || 'Failed to update category';
            })
            // Delete Category
            .addCase(deleteCategory.pending, (state) => {
                state.categoryLoading = true;
                state.error = null;
            })
            .addCase(deleteCategory.fulfilled, (state, action) => {
                state.categoryLoading = false;
                state.success = true;
            })
            .addCase(deleteCategory.rejected, (state, action) => {
                state.categoryLoading = false;
                state.error = action.payload?.error || 'Failed to delete category';
            })
            // Create Subcategory
            .addCase(createSubcategory.pending, (state) => {
                state.subcategoryLoading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(createSubcategory.fulfilled, (state, action) => {
                state.subcategoryLoading = false;
                state.success = true;
            })
            .addCase(createSubcategory.rejected, (state, action) => {
                state.subcategoryLoading = false;
                state.error = action.payload?.error || 'Failed to create subcategory';
            })
            // Get Subcategories by Category
            .addCase(getSubcategoriesByCategory.pending, (state) => {
                state.subcategoryLoading = true;
                state.error = null;
            })
            .addCase(getSubcategoriesByCategory.fulfilled, (state, action) => {
                state.subcategoryLoading = false;
                const { categoryId, data } = action.payload;
                // Handle API response structure: { subCategories: [...] } (capital C) or { subcategories: [...] } or { data: [...] }
                const subcategories = data?.subCategories || data?.subcategories || data?.data || (Array.isArray(data) ? data : []);
                state.subcategories[categoryId] = subcategories;

                // Also update the category in supportCategories if it exists
                const categoryIndex = state.supportCategories.findIndex(cat => (cat.categoryId || cat.id) === categoryId);
                if (categoryIndex !== -1) {
                    state.supportCategories[categoryIndex].subCategories = subcategories;
                }
            })
            .addCase(getSubcategoriesByCategory.rejected, (state, action) => {
                state.subcategoryLoading = false;
                state.error = action.payload?.error || 'Failed to fetch subcategories';
            })
            // Get All Support Subcategories (flat list)
            .addCase(getAllSupportSubCategories.fulfilled, (state, action) => {
                const list = action.payload?.subCategories || action.payload?.data || (Array.isArray(action.payload) ? action.payload : []);
                state.allSupportSubCategories = list;
            })
            .addCase(getAllSupportSubCategories.rejected, (state, action) => {
                state.error = action.payload?.error || 'Failed to fetch all subcategories';
            })
            // Update Subcategory
            .addCase(updateSubcategory.pending, (state) => {
                state.subcategoryLoading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(updateSubcategory.fulfilled, (state, action) => {
                state.subcategoryLoading = false;
                state.success = true;
            })
            .addCase(updateSubcategory.rejected, (state, action) => {
                state.subcategoryLoading = false;
                state.error = action.payload?.error || 'Failed to update subcategory';
            })
            // Delete Subcategory
            .addCase(deleteSubcategory.pending, (state) => {
                state.subcategoryLoading = true;
                state.error = null;
            })
            .addCase(deleteSubcategory.fulfilled, (state, action) => {
                state.subcategoryLoading = false;
                state.success = true;
            })
            .addCase(deleteSubcategory.rejected, (state, action) => {
                state.subcategoryLoading = false;
                state.error = action.payload?.error || 'Failed to delete subcategory';
            })
            // ==================== SPECIALITY CATEGORY REDUCERS ====================
            // Create Speciality Category
            .addCase(createSpCategory.pending, (state) => {
                state.spCategoryCreateLoading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(createSpCategory.fulfilled, (state) => {
                state.spCategoryCreateLoading = false;
                state.success = true;
            })
            .addCase(createSpCategory.rejected, (state, action) => {
                state.spCategoryCreateLoading = false;
                state.error = action.payload?.error || 'Failed to create speciality category';
            })
            // Get Speciality Categories
            .addCase(getSpCategories.pending, (state) => {
                state.spCategoryLoading = true;
                state.error = null;
            })
            .addCase(getSpCategories.fulfilled, (state, action) => {
                state.spCategoryLoading = false;
                state.spCategories = action.payload?.categories || action.payload?.data || [];
                if (action.payload) {
                    state.spCategoryPagination = {
                        total: action.payload.total || 0,
                        page: action.payload.page || 1,
                        limit: action.payload.limit || 10,
                        totalPages: action.payload.totalPages || 0
                    };
                }
            })
            .addCase(getSpCategories.rejected, (state, action) => {
                state.spCategoryLoading = false;
                state.error = action.payload?.error || 'Failed to fetch speciality categories';
            })
            // Update Speciality Category
            .addCase(updateSpCategory.pending, (state) => {
                state.spCategoryUpdateLoading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(updateSpCategory.fulfilled, (state, action) => {
                state.spCategoryUpdateLoading = false;
                state.success = true;
                const updated = action.payload?.category || action.payload?.data || action.payload;
                if (updated) {
                    const id = updated.spcategoryId;
                    const index = state.spCategories.findIndex(c => c.spcategoryId === id);
                    if (index !== -1) {
                        state.spCategories[index] = { ...state.spCategories[index], ...updated };
                    }
                }
            })
            .addCase(updateSpCategory.rejected, (state, action) => {
                state.spCategoryUpdateLoading = false;
                state.error = action.payload?.error || 'Failed to update speciality category';
            })
            // Delete Speciality Category
            .addCase(deleteSpCategory.pending, (state) => {
                state.spCategoryDeleteLoading = true;
                state.error = null;
            })
            .addCase(deleteSpCategory.fulfilled, (state, action) => {
                state.spCategoryDeleteLoading = false;
                state.success = true;
                const deletedId = action.meta.arg;
                state.spCategories = state.spCategories.filter(c => c.spcategoryId !== deletedId);
            })
            .addCase(deleteSpCategory.rejected, (state, action) => {
                state.spCategoryDeleteLoading = false;
                state.error = action.payload?.error || 'Failed to delete speciality category';
            })
            // Toggle Speciality Category Status
            .addCase(toggleSpCategoryStatus.pending, (state) => {
                state.spCategoryToggleLoading = true;
                state.error = null;
            })
            .addCase(toggleSpCategoryStatus.fulfilled, (state) => {
                state.spCategoryToggleLoading = false;
                state.success = true;
            })
            .addCase(toggleSpCategoryStatus.rejected, (state, action) => {
                state.spCategoryToggleLoading = false;
                state.error = action.payload?.error || 'Failed to toggle speciality category status';
            })
            // Check Speciality Category Name
            .addCase(checkSpCategoryName.pending, (state) => {
                state.spCategoryNameCheckLoading = true;
                state.spCategoryNameCheckResult = null;
            })
            .addCase(checkSpCategoryName.fulfilled, (state, action) => {
                state.spCategoryNameCheckLoading = false;
                state.spCategoryNameCheckResult = action.payload;
            })
            .addCase(checkSpCategoryName.rejected, (state) => {
                state.spCategoryNameCheckLoading = false;
                state.spCategoryNameCheckResult = null;
            })
            // Get Active Speciality Category List (dropdown)
            .addCase(getSpCategoryList.pending, (state) => {
                state.spCategoryListLoading = true;
            })
            .addCase(getSpCategoryList.fulfilled, (state, action) => {
                state.spCategoryListLoading = false;
                state.spCategoryList = action.payload?.categories || action.payload?.data || [];
            })
            .addCase(getSpCategoryList.rejected, (state) => {
                state.spCategoryListLoading = false;
                state.spCategoryList = [];
            })
            // Download Sample Speciality Category Excel
            .addCase(downloadSpCategorySampleExcel.pending, (state) => {
                state.spCategoryDownloadLoading = true;
            })
            .addCase(downloadSpCategorySampleExcel.fulfilled, (state) => {
                state.spCategoryDownloadLoading = false;
            })
            .addCase(downloadSpCategorySampleExcel.rejected, (state) => {
                state.spCategoryDownloadLoading = false;
            })
            // Bulk Upload Speciality Categories
            .addCase(bulkUploadSpCategories.pending, (state) => {
                state.spCategoryBulkUploadLoading = true;
                state.error = null;
            })
            .addCase(bulkUploadSpCategories.fulfilled, (state) => {
                state.spCategoryBulkUploadLoading = false;
                state.success = true;
            })
            .addCase(bulkUploadSpCategories.rejected, (state, action) => {
                state.spCategoryBulkUploadLoading = false;
                state.error = action.payload?.error || 'Failed to bulk upload speciality categories';
            })
            // ==================== SPECIALTY REDUCERS ====================
            // Create Specialty
            .addCase(createSpecialty.pending, (state) => {
                state.specialtyLoading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(createSpecialty.fulfilled, (state, action) => {
                state.specialtyLoading = false;
                state.success = true;
            })
            .addCase(createSpecialty.rejected, (state, action) => {
                state.specialtyLoading = false;
                state.error = action.payload?.error || 'Failed to create specialty';
            })
            // Get Specialties
            .addCase(getSpecialties.pending, (state) => {
                state.specialtyLoading = true;
                state.error = null;
            })
            .addCase(getSpecialties.fulfilled, (state, action) => {
                state.specialtyLoading = false;
                // Handle API response structure: { data: [...], total, page, limit, totalPages }
                const specialties = action.payload?.data || [];
                state.specialties = specialties;
                // Store pagination metadata
                if (action.payload) {
                    state.specialtyPagination = {
                        total: action.payload.total || 0,
                        page: action.payload.page || 1,
                        limit: action.payload.limit || 12,
                        totalPages: action.payload.totalPages || 0
                    };
                }
            })
            .addCase(getSpecialties.rejected, (state, action) => {
                state.specialtyLoading = false;
                state.error = action.payload?.error || 'Failed to fetch specialties';
            })
            // Get Specialty by ID
            .addCase(getSpecialtyById.pending, (state) => {
                state.specialtyLoading = true;
                state.error = null;
            })
            .addCase(getSpecialtyById.fulfilled, (state, action) => {
                state.specialtyLoading = false;
            })
            .addCase(getSpecialtyById.rejected, (state, action) => {
                state.specialtyLoading = false;
                state.error = action.payload?.error || 'Failed to fetch specialty';
            })
            // Update Specialty
            .addCase(updateSpecialty.pending, (state) => {
                state.specialtyUpdateLoading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(updateSpecialty.fulfilled, (state, action) => {
                state.specialtyUpdateLoading = false;
                state.success = true;
                // Update the specialty in the list
                const updatedSpecialty = action.payload?.speciality || action.payload?.data || action.payload;
                if (updatedSpecialty) {
                    const id = updatedSpecialty._id || updatedSpecialty.specialityId;
                    const index = state.specialties.findIndex(s =>
                        (s._id === id) || (s.specialityId === id) || (s._id === updatedSpecialty._id) || (s.specialityId === updatedSpecialty.specialityId)
                    );
                    if (index !== -1) {
                        state.specialties[index] = { ...state.specialties[index], ...updatedSpecialty };
                    }
                }
            })
            .addCase(updateSpecialty.rejected, (state, action) => {
                state.specialtyUpdateLoading = false;
                state.error = action.payload?.error || 'Failed to update specialty';
            })
            // Delete Specialty
            .addCase(deleteSpecialty.pending, (state) => {
                state.specialtyDeleteLoading = true;
                state.error = null;
            })
            .addCase(deleteSpecialty.fulfilled, (state, action) => {
                state.specialtyDeleteLoading = false;
                state.success = true;
                // Remove the specialty from the list
                const deletedId = action.meta.arg;
                state.specialties = state.specialties.filter(s =>
                    (s._id !== deletedId) && (s.specialtyId !== deletedId) && (s.specialityId !== deletedId)
                );
            })
            .addCase(deleteSpecialty.rejected, (state, action) => {
                state.specialtyDeleteLoading = false;
                state.error = action.payload?.error || 'Failed to delete specialty';
            })
            // Toggle Specialty Status
            .addCase(toggleSpecialtyStatus.pending, (state) => {
                state.specialtyToggleLoading = true;
                state.error = null;
            })
            .addCase(toggleSpecialtyStatus.fulfilled, (state, action) => {
                state.specialtyToggleLoading = false;
                state.success = true;
            })
            .addCase(toggleSpecialtyStatus.rejected, (state, action) => {
                state.specialtyToggleLoading = false;
                state.error = action.payload?.error || 'Failed to toggle specialty status';
            })
            // Get All Specialty List
            .addCase(getallspecilitylist.pending, (state) => {
                state.specialtyLoading = true;
                state.error = null;
            })
            .addCase(getallspecilitylist.fulfilled, (state, action) => {
                state.specialtyLoading = false;
                // Handle API response structure: { success: true, specialties: [...] }
                const specialties = action.payload?.specialties || action.payload?.data || (Array.isArray(action.payload) ? action.payload : []);
                state.specialties = specialties;
            })
            .addCase(getallspecilitylist.rejected, (state, action) => {
                state.specialtyLoading = false;
                state.error = action.payload?.error || 'Failed to fetch specialty list';
            })
            // Check Specialty Name
            .addCase(checkSpecilityName.pending, (state) => {
                state.specialtyNameCheckLoading = true;
                state.specialtyNameCheckResult = null;
            })
            .addCase(checkSpecilityName.fulfilled, (state, action) => {
                state.specialtyNameCheckLoading = false;
                state.specialtyNameCheckResult = action.payload;
            })
            .addCase(checkSpecilityName.rejected, (state, action) => {
                state.specialtyNameCheckLoading = false;
                state.specialtyNameCheckResult = null;
            })
            // Download Sample Specialty Excel
            .addCase(downloadSpecialtySampleExcel.pending, (state) => {
                state.specialtyDownloadLoading = true;
            })
            .addCase(downloadSpecialtySampleExcel.fulfilled, (state) => {
                state.specialtyDownloadLoading = false;
            })
            .addCase(downloadSpecialtySampleExcel.rejected, (state) => {
                state.specialtyDownloadLoading = false;
            })
            // Bulk Upload Specialties
            .addCase(bulkUploadSpecialties.pending, (state) => {
                state.specialtyBulkUploadLoading = true;
                state.error = null;
            })
            .addCase(bulkUploadSpecialties.fulfilled, (state) => {
                state.specialtyBulkUploadLoading = false;
                state.success = true;
            })
            .addCase(bulkUploadSpecialties.rejected, (state, action) => {
                state.specialtyBulkUploadLoading = false;
                state.error = action.payload?.error || 'Failed to bulk upload specialties';
            })
            // ==================== PROFESSION REDUCERS ====================
            // Create Profession
            .addCase(createProfession.pending, (state) => {
                state.professionCreateLoading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(createProfession.fulfilled, (state) => {
                state.professionCreateLoading = false;
                state.success = true;
            })
            .addCase(createProfession.rejected, (state, action) => {
                state.professionCreateLoading = false;
                state.error = action.payload?.error || 'Failed to create profession';
            })
            // Get Professions
            .addCase(getProfessions.pending, (state) => {
                state.professionLoading = true;
                state.error = null;
            })
            .addCase(getProfessions.fulfilled, (state, action) => {
                state.professionLoading = false;
                const professions = action.payload?.professions || action.payload?.data || [];
                state.professions = professions;
                if (action.payload) {
                    state.professionPagination = {
                        total: action.payload.total || 0,
                        page: action.payload.page || 1,
                        limit: action.payload.limit || 12,
                        totalPages: action.payload.totalPages || 0
                    };
                }
            })
            .addCase(getProfessions.rejected, (state, action) => {
                state.professionLoading = false;
                state.error = action.payload?.error || 'Failed to fetch professions';
            })
            // Get Profession by ID
            .addCase(getProfessionById.pending, (state) => {
                state.professionLoading = true;
                state.error = null;
            })
            .addCase(getProfessionById.fulfilled, (state) => {
                state.professionLoading = false;
            })
            .addCase(getProfessionById.rejected, (state, action) => {
                state.professionLoading = false;
                state.error = action.payload?.error || 'Failed to fetch profession';
            })
            // Update Profession
            .addCase(updateProfession.pending, (state) => {
                state.professionUpdateLoading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(updateProfession.fulfilled, (state, action) => {
                state.professionUpdateLoading = false;
                state.success = true;
                const updatedProfession = action.payload?.profession || action.payload?.data || action.payload;
                if (updatedProfession) {
                    const id = updatedProfession.professionId;
                    const index = state.professions.findIndex(p => p.professionId === id);
                    if (index !== -1) {
                        state.professions[index] = { ...state.professions[index], ...updatedProfession };
                    }
                }
            })
            .addCase(updateProfession.rejected, (state, action) => {
                state.professionUpdateLoading = false;
                state.error = action.payload?.error || 'Failed to update profession';
            })
            // Delete Profession
            .addCase(deleteProfession.pending, (state) => {
                state.professionDeleteLoading = true;
                state.error = null;
            })
            .addCase(deleteProfession.fulfilled, (state, action) => {
                state.professionDeleteLoading = false;
                state.success = true;
                const deletedId = action.meta.arg;
                state.professions = state.professions.filter(p => p.professionId !== deletedId);
            })
            .addCase(deleteProfession.rejected, (state, action) => {
                state.professionDeleteLoading = false;
                state.error = action.payload?.error || 'Failed to delete profession';
            })
            // Toggle Profession Status
            .addCase(toggleProfessionStatus.pending, (state) => {
                state.professionToggleLoading = true;
                state.error = null;
            })
            .addCase(toggleProfessionStatus.fulfilled, (state) => {
                state.professionToggleLoading = false;
                state.success = true;
            })
            .addCase(toggleProfessionStatus.rejected, (state, action) => {
                state.professionToggleLoading = false;
                state.error = action.payload?.error || 'Failed to toggle profession status';
            })
            // Check Profession Name
            .addCase(checkProfessionName.pending, (state) => {
                state.professionNameCheckLoading = true;
                state.professionNameCheckResult = null;
            })
            .addCase(checkProfessionName.fulfilled, (state, action) => {
                state.professionNameCheckLoading = false;
                state.professionNameCheckResult = action.payload;
            })
            .addCase(checkProfessionName.rejected, (state) => {
                state.professionNameCheckLoading = false;
                state.professionNameCheckResult = null;
            })
            // Download Sample Profession Excel
            .addCase(downloadProfessionSampleExcel.pending, (state) => {
                state.professionDownloadLoading = true;
            })
            .addCase(downloadProfessionSampleExcel.fulfilled, (state) => {
                state.professionDownloadLoading = false;
            })
            .addCase(downloadProfessionSampleExcel.rejected, (state) => {
                state.professionDownloadLoading = false;
            })
            // Bulk Upload Professions
            .addCase(bulkUploadProfessions.pending, (state) => {
                state.professionBulkUploadLoading = true;
                state.error = null;
            })
            .addCase(bulkUploadProfessions.fulfilled, (state) => {
                state.professionBulkUploadLoading = false;
                state.success = true;
            })
            .addCase(bulkUploadProfessions.rejected, (state, action) => {
                state.professionBulkUploadLoading = false;
                state.error = action.payload?.error || 'Failed to bulk upload professions';
            })
            // Country bulk upload
            .addCase(downloadCountrySampleExcel.pending, (state) => { state.countryDownloadLoading = true; })
            .addCase(downloadCountrySampleExcel.fulfilled, (state) => { state.countryDownloadLoading = false; })
            .addCase(downloadCountrySampleExcel.rejected, (state) => { state.countryDownloadLoading = false; })
            .addCase(bulkUploadCountries.pending, (state) => { state.countryBulkUploadLoading = true; state.error = null; })
            .addCase(bulkUploadCountries.fulfilled, (state) => { state.countryBulkUploadLoading = false; state.success = true; })
            .addCase(bulkUploadCountries.rejected, (state, action) => {
                state.countryBulkUploadLoading = false;
                state.error = action.payload?.error || 'Failed to bulk upload countries';
            })
            // Support bulk upload
            .addCase(downloadSupportSampleExcel.pending, (state) => { state.supportDownloadLoading = true; })
            .addCase(downloadSupportSampleExcel.fulfilled, (state) => { state.supportDownloadLoading = false; })
            .addCase(downloadSupportSampleExcel.rejected, (state) => { state.supportDownloadLoading = false; })
            .addCase(bulkUploadSupport.pending, (state) => { state.supportBulkUploadLoading = true; state.error = null; })
            .addCase(bulkUploadSupport.fulfilled, (state) => { state.supportBulkUploadLoading = false; state.success = true; })
            .addCase(bulkUploadSupport.rejected, (state, action) => {
                state.supportBulkUploadLoading = false;
                state.error = action.payload?.error || 'Failed to bulk upload support';
            })
            // Get All Professions List (for dropdowns)
            .addCase(getAllProfessionsList.pending, (state) => {
                state.professionListAllLoading = true;
            })
            .addCase(getAllProfessionsList.fulfilled, (state, action) => {
                state.professionListAllLoading = false;
                state.professionListAll = action.payload?.professions || action.payload?.data || [];
            })
            .addCase(getAllProfessionsList.rejected, (state) => {
                state.professionListAllLoading = false;
                state.professionListAll = [];
            })
            // ==================== COUNTRY REDUCERS ====================
            // Get Countries
            .addCase(getCountries.pending, (state) => {
                state.countryLoading = true;
                state.error = null;
            })
            .addCase(getCountries.fulfilled, (state, action) => {
                state.countryLoading = false;
                state.countries = action.payload?.data || [];
                if (action.payload) {
                    state.countryPagination = {
                        total: action.payload.total || 0,
                        page: action.payload.page || 1,
                        limit: action.payload.limit || 10,
                        totalPages: action.payload.totalPages || 0
                    };
                }
            })
            .addCase(getCountries.rejected, (state, action) => {
                state.countryLoading = false;
                state.error = action.payload?.error || 'Failed to fetch countries';
            })
            // Get Country by ID
            .addCase(getCountryById.pending, (state) => {
                state.countryLoading = true;
                state.error = null;
            })
            .addCase(getCountryById.fulfilled, (state, action) => {
                state.countryLoading = false;
            })
            .addCase(getCountryById.rejected, (state, action) => {
                state.countryLoading = false;
                state.error = action.payload?.error || 'Failed to fetch country';
            })
            // Create Country
            .addCase(createCountry.pending, (state) => {
                state.countryCreateLoading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(createCountry.fulfilled, (state) => {
                state.countryCreateLoading = false;
                state.success = true;
            })
            .addCase(createCountry.rejected, (state, action) => {
                state.countryCreateLoading = false;
                state.error = action.payload?.error || 'Failed to create country';
            })
            // Update Country
            .addCase(updateCountry.pending, (state) => {
                state.countryUpdateLoading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(updateCountry.fulfilled, (state) => {
                state.countryUpdateLoading = false;
                state.success = true;
            })
            .addCase(updateCountry.rejected, (state, action) => {
                state.countryUpdateLoading = false;
                state.error = action.payload?.error || 'Failed to update country';
            })
            // Delete Country
            .addCase(deleteCountry.pending, (state) => {
                state.countryDeleteLoading = true;
                state.error = null;
            })
            .addCase(deleteCountry.fulfilled, (state, action) => {
                state.countryDeleteLoading = false;
                state.success = true;
                const deletedId = action.meta.arg;
                state.countries = state.countries.filter(c => c.countryId !== deletedId);
            })
            .addCase(deleteCountry.rejected, (state, action) => {
                state.countryDeleteLoading = false;
                state.error = action.payload?.error || 'Failed to delete country';
            })
            // Toggle Country Status
            .addCase(toggleCountryStatus.pending, (state) => {
                state.countryToggleLoading = true;
                state.error = null;
            })
            .addCase(toggleCountryStatus.fulfilled, (state) => {
                state.countryToggleLoading = false;
                state.success = true;
            })
            .addCase(toggleCountryStatus.rejected, (state, action) => {
                state.countryToggleLoading = false;
                state.error = action.payload?.error || 'Failed to toggle country status';
            })
            // Check Country Name Duplicate
            .addCase(checkCountryNameDuplicate.pending, (state) => {
                state.countryNameCheckLoading = true;
                state.countryNameCheckResult = null;
            })
            .addCase(checkCountryNameDuplicate.fulfilled, (state, action) => {
                state.countryNameCheckLoading = false;
                state.countryNameCheckResult = action.payload;
            })
            .addCase(checkCountryNameDuplicate.rejected, (state) => {
                state.countryNameCheckLoading = false;
                state.countryNameCheckResult = null;
            })
            // Get Country Timezone List (for doctor/patient creation & registration)
            .addCase(docPatCreationCountryList.pending, (state) => {
                state.countryTimezoneLoading = true;
                state.error = null;
            })
            .addCase(docPatCreationCountryList.fulfilled, (state, action) => {
                state.countryTimezoneLoading = false;
                state.countryTimezoneList = action.payload?.data || action.payload || [];
            })
            .addCase(docPatCreationCountryList.rejected, (state, action) => {
                state.countryTimezoneLoading = false;
                state.error = action.payload?.error || 'Failed to fetch country timezone list';
            });
    }
});

export const { clearError, clearSuccess, clearSubcategories, clearSpecialtyNameCheck, clearSpCategoryNameCheck, clearProfessionNameCheck, clearCountryNameCheck } = masterSlice.actions;
export default masterSlice.reducer;

